import React from 'react';
import LocationSearchBox from 'location-search-box';
import format from 'date-fns/format';
import compose from 'lodash/fp/compose';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import filter from 'lodash/filter';
import unionBy from 'lodash/unionBy';
import trim from 'lodash/trim';
import styles from './SearchArea.scss';
import { generateSearchUrl } from '../../../shared/model/transformers/srpSearchConditionUrlGenerator';
import { channelObjects } from '../../../shared/model/constants/channels';
import { getPropertyTypeFromId } from '../../../shared/model/constants/propertyTypes';
import priceOptions from '../../searchResult/refinements/constants/priceOptions';
import pricePerAnnumOptions from '../../searchResult/refinements/constants/pricePerAnnumOptions';
import floorAreaOptions from '../../searchResult/refinements/constants/floorAreaOptions';
import quickLinks from '../../../shared/model/constants/quickLinks';
import ChannelTabs from './ChannelTabs';
import SearchFilters from './SearchFilters';
import { decode, parseLocation, stringifyLocation } from '../util/codec';
import { getHeadingForChannel } from '../model/metaData';
import * as referrerService from '../util/referrerService';
import { getCookie, setCookie } from '../../../client/cookie/cookieService';
import httpService from '../../httpService';
import { logError } from '../../../shared/log';
import { USER_AGENT_PROP_TYPES, RECENT_SEARCH_PROP_TYPES } from '../model/types';
import { CHANNEL_PROP_TYPES } from '../../../shared/model/types';
import { saveRecentSearch } from '../../../client/service/preferenceService';
import RecentSearchesContainer from './RecentSearchesContainer';
import history from '../../../client/service/history';
import trackingAdapter from '../tracking/trackingAdapter';
import { withUserAgent } from '../../../shared/userAgentContext';
import { withRelease } from '../../../shared/releaseContext';
import bffRequest from '../../../shared/requests/bffRequest';
import { parseConfig } from '../../../shared/config';
import { FEATURE_FLAGS_PROP_TYPES } from '../../../shared/experiments/phasedRelease/config';

const { ForSale, Sold, ForLease, Invest } = channelObjects;

export class SearchArea extends React.Component {
  static propTypes = {
    currentChannel: CHANNEL_PROP_TYPES.isRequired,
    userAgent: USER_AGENT_PROP_TYPES.isRequired,
    recentSearches: RECENT_SEARCH_PROP_TYPES,
    featureFlags: FEATURE_FLAGS_PROP_TYPES,
  }

  constructor(props) {
    super(props);
    this.state = {
      minPriceSelectedOptionForSaleSold: priceOptions[0],
      minPriceSelectedOptionForLease: pricePerAnnumOptions[0],
      maxPriceSelectedOptionForSaleSold: priceOptions[0],
      maxPriceSelectedOptionForLease: pricePerAnnumOptions[0],
      minFloorAreaSelectedOption: floorAreaOptions[0],
      maxFloorAreaSelectedOption: floorAreaOptions[0],
      propertyTypes: [],
    };
  }

  componentDidMount() {
    let recentLocations;

    try {
      recentLocations = JSON.parse(decode(getCookie('recent_locations') || '[]'));
    } catch (err) {
      logError(err);
      recentLocations = null;
    }

    if (recentLocations) {
      if (isArray(recentLocations) && recentLocations.length > 0) {
        this.locationBox.locations = recentLocations[0]['_locationValue'].split('; ') // eslint-disable-line
          .filter(locationStr => !isEmpty(locationStr))
          .map(locationStr => ({
            displayValue: stringifyLocation(parseLocation(locationStr)),
          }));
      }
    }
  }

  getUniqueId() {
    return new Date().getTime() + Math.random().toString(36).substring(7);
  }

  setPreferredCookie() {
    const preferredChannel = this.props.currentChannel.id;
    const preferredState = this.firstLocationState();

    setCookie('preferred_state', preferredState, { expires: 30 });
    setCookie('preferred_channel', preferredChannel, { expires: 30 });

    if (this.locationBox.locations && this.locationBox.locations.length > 0) {
      const currentLocations = {
        _lastUsedTimestamp: format(new Date(), 'mmm d, yyyy h:MM:ss TT'),
        _locationValue: this.locationBox.locations.map(location => location.displayValue).join('; '),
        _maxLocationValueLength: 0,
        _latitude: 0.0,
        _longitude: 0.0,
        _flags: 0,
      };

      let prevRecentLocations;

      try {
        prevRecentLocations = JSON.parse(decode(getCookie('recent_locations') || '[]'));
      } catch (err) {
        logError(err);
        prevRecentLocations = [];
      }

      setCookie('recent_locations', JSON.stringify([currentLocations].concat(prevRecentLocations).slice(0, 7)), {
        expires: 30,
      });
    }
  }

  setReferrerForInvest() {
    if (this.props.currentChannel === Invest) {
      referrerService.referAsPrimary();
    }
  }

  setQuickLinkReferrerForInvest() {
    if (this.props.currentChannel === Invest) {
      referrerService.referAsQuickLink();
    }
  }

  locationsExcludeFreeSearch = locations => filter(locations, location => location.indexOf(',') !== -1);

  refinementsFromFilters() {
    const {
      propertyTypes,
      minPriceSelectedOptionForSaleSold,
      minPriceSelectedOptionForLease,
      maxPriceSelectedOptionForSaleSold,
      maxPriceSelectedOptionForLease,
      minFloorAreaSelectedOption,
      maxFloorAreaSelectedOption,
    } = this.state;
    const { currentChannel } = this.props;

    const locations = this.locationBox.locations.map(location => location.displayValue);
    const isCurrentLocationEmpty = this.locationBox.currentLocation.length === 0;
    const where = isCurrentLocationEmpty
      ? ''
      : this.locationBox.currentLocation.join(',');

    // Note: this also makes sure that Invest and Leased will get undefined
    const minPriceMapping = {
      [ForSale.id]: minPriceSelectedOptionForSaleSold.value,
      [Sold.id]: minPriceSelectedOptionForSaleSold.value,
      [ForLease.id]: minPriceSelectedOptionForLease.value,
    };

    // Note: this also makes sure that Invest and Leased will get undefined
    const maxPriceMapping = {
      [ForSale.id]: maxPriceSelectedOptionForSaleSold.value,
      [Sold.id]: maxPriceSelectedOptionForSaleSold.value,
      [ForLease.id]: maxPriceSelectedOptionForLease.value,
    };

    const propertyTypeUrls = propertyTypes.map(type => type.url);

    const isNotInvest = currentChannel !== Invest;

    const pathFields = {
      channel: this.props.currentChannel.url,
      propertyTypes: isNotInvest && propertyTypeUrls,
      locations,
      minPrice: minPriceMapping[currentChannel.id],
      maxPrice: maxPriceMapping[currentChannel.id],
    };

    const queryFields = {
      minFloorArea: isNotInvest && minFloorAreaSelectedOption.value,
      maxFloorArea: isNotInvest && maxFloorAreaSelectedOption.value,
      currentLocation: where,
    };

    return { pathFields, queryFields };
  }

  firstLocationState() {
    if (this.locationBox.locations.length > 0) {
      const firstLocation = this.locationBox.locations.map(location => location.displayValue)[0];
      const matchResult = firstLocation.match(/,\s[\w]*/g);
      return matchResult
        ? matchResult[0].replace(', ', '').toLowerCase()
        : 'null';
    }
    return 'null';
  }

  /*
   * This function is called so SRP can track unique searches. The cookie is read and set
   * in listing-ui as well
   */
  generateSearchSerialId() {
    const SEARCH_SERIAL_ID = 'search-serial-id';
    const serialId = this.getUniqueId();
    setCookie(SEARCH_SERIAL_ID, serialId, { expires: 7 });
  }

  render() {
    const {
      propertyTypes,
      minPriceSelectedOptionForSaleSold,
      minPriceSelectedOptionForLease,
      maxPriceSelectedOptionForSaleSold,
      maxPriceSelectedOptionForLease,
      minFloorAreaSelectedOption,
      maxFloorAreaSelectedOption,
    } = this.state;
    const { featureFlags } = this.props;

    const { currentChannel, recentSearches, userAgent } = this.props;

    const submitSearch = () => {
      this.setPreferredCookie();
      this.setReferrerForInvest();
      this.generateSearchSerialId();
      const liveLocation = trim(this.locationBox.liveLocation);
      const { pathFields, queryFields } = this.refinementsFromFilters();
      const searchUrl = generateSearchUrl(pathFields, queryFields);
      if (liveLocation.length > 1) {
        this.locationBox.locations = unionBy(this.locationBox.locations, [{
          displayValue: liveLocation,
        }], 'displayValue');
      }
      saveRecentSearch(searchUrl);

      history.push(searchUrl);
    };

    const trackUserAction = (message) => {
      trackingAdapter.searchAreaUserAction({ userAction: message, currentChannel, userAgent });
    };

    const trackOmnitureNearby = () => {
      trackUserAction('nearby suburbs suggestion search');
    };

    const trackOmnitureQuickSerach = () => {
      trackUserAction('state quick link');
    };

    const quickSearch = (quickLink) => {
      trackOmnitureQuickSerach();
      this.setQuickLinkReferrerForInvest();
      const channel = this.props.currentChannel.url;
      const quickLinkUrl = generateSearchUrl({ channel, locations: [quickLink.searchValue] });
      history.push(quickLinkUrl);
    };

    const getSmartSuggestResult = (locationInput) => {
      const {
        SMART_SUGGEST_ENDPOINT,
      } = parseConfig();
      const smartSuggestUrl = `${SMART_SUGGEST_ENDPOINT}/smart-suggest?query=`;

      return httpService.request({
        path: `${smartSuggestUrl}${locationInput}`,
        method: 'GET',
      }).then(data => data.suggestions);
    };

    const getNearbySuburbResult = locations => bffRequest({
      path: 'nearby-suburbs',
      method: 'GET',
      query: {
        location: this.locationsExcludeFreeSearch(locations).join('%3B'),
        featureFlags,
      },
    });

    const trackRecentSearch = () => {
      trackingAdapter.trackRecentSearchClick({ channelObject: currentChannel });
    };

    return (
      <div className={styles.backgroundWrap}>
        <picture className={styles.hero}>
          <source
            media="(min-width: 768px)"
            srcSet={`/listing-ui-assets/img/homepage/hero-img-${currentChannel.id}-large.webp?version=2 1920w`}
            type="image/webp"
          />
          <source
            srcSet={`/listing-ui-assets/img/homepage/hero-img-${currentChannel.id}-small.webp?version=2 750w`}
            type="image/webp"
          />
          <img
            className={styles.heroImage}
            alt="alt text"
            srcSet={`
              /listing-ui-assets/img/homepage/hero-img-${currentChannel.id}-small.jpg?version=2 750w,
              /listing-ui-assets/img/homepage/hero-img-${currentChannel.id}-large.jpg?version=2 1920w
            `}
            sizes="750w, (min-width: 768px) 1920px"
            src={`/listing-ui-assets/img/homepage/hero-img-${currentChannel.id}-small.jpg?version=2 750w`}
          />
        </picture>
        <div className={styles.container}>
          <h1 className={styles.heading}>The #1 place for commercial property</h1>
          <form className={styles.searchForm} autoComplete="off">
            <h2 className={styles.searchLabel}>{getHeadingForChannel(currentChannel)}</h2>
            <ChannelTabs currentChannel={currentChannel} />
            <div className={styles.searchWrapper}>
              <LocationSearchBox
                getSmartSuggestResult={getSmartSuggestResult}
                getNearbySuburbResult={getNearbySuburbResult}
                userAgent={userAgent}
                selectNearby={trackOmnitureNearby}
                isScrollToViewTop
                quickLinks={quickLinks}
                onClickQuickLink={quickSearch}
                ref={(ref) => { this.locationBox = ref; }}
              />
              <button
                className={styles.submitButton}
                onClick={submitSearch}
                type="button"
                ref={(ref) => { this.searchBtnOnDesktop = ref; }}
              >
                Search
              </button>
            </div>
            <SearchFilters
              currentChannel={currentChannel}
              selectedPropertyTypes={propertyTypes}
              minPriceSelectedOptionForSaleSold={minPriceSelectedOptionForSaleSold}
              minPriceSelectedOptionForLease={minPriceSelectedOptionForLease}
              maxPriceSelectedOptionForSaleSold={maxPriceSelectedOptionForSaleSold}
              maxPriceSelectedOptionForLease={maxPriceSelectedOptionForLease}
              minFloorAreaSelectedOption={minFloorAreaSelectedOption}
              maxFloorAreaSelectedOption={maxFloorAreaSelectedOption}
              onPropertyTypesChange={(selectedPropertyTypes) => {
                this.setState({ propertyTypes: selectedPropertyTypes.map(getPropertyTypeFromId) });
              }}
              onMinPriceChangeForSaleSold={option => (
                this.setState({ minPriceSelectedOptionForSaleSold: option })
              )}
              onMinPriceChangeForLease={option => (
                this.setState({ minPriceSelectedOptionForLease: option })
              )}
              onMaxPriceChangeForSaleSold={option => (
                this.setState({ maxPriceSelectedOptionForSaleSold: option })
              )}
              onMaxPriceChangeForLease={option => (
                this.setState({ maxPriceSelectedOptionForLease: option })
              )}
              onMinFloorAreaChange={option => (
                this.setState({ minFloorAreaSelectedOption: option })
              )}
              onMaxFloorAreaChange={option => (
                this.setState({ maxFloorAreaSelectedOption: option })
              )}
            />
            <button
              className={styles.largeSubmitButton}
              onClick={submitSearch}
              type="button"
              ref={(ref) => { this.searchBtnOnMobile = ref; }}
            >Search
            </button>
          </form>
          <RecentSearchesContainer
            trackRecentSearchClick={trackRecentSearch}
            recentSearches={recentSearches}
          />
        </div>
      </div>
    );
  }
}

export default compose(withRelease(), withUserAgent())(SearchArea);
